import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { Box, ListItemText, Menu, MenuItem, Skeleton, Typography } from '@mui/material';
import IRDataGrid from 'features/IRWorkstation/components/Common/IRDataGrid';
import FundraisingDrawer from 'features/IRWorkstation/components/Fundraising/FundraisingDrawer';
import buildFundraisingGridColumns from 'features/IRWorkstation/components/Fundraising/fundraisingGridColumns';
import { FUNDRAISING_COLUMNS } from 'features/IRWorkstation/constants/fundraisingColumns';
import salesforceFundraisingsService from 'services/salesforceFundraisingsService';
import { useNotificationContext } from 'contexts/NotificationContext';
import { useIRWorkstation } from 'features/IRWorkstation/contexts/IRWorkstationContext';

// The shared fundraising table card used by the Raise Dashboard "LP Pipeline"
// table and the LP360 "Fundraising opportunities" card: title bar, filter bar,
// grid (fixed column headers, server-side sort, infinite scroll), the row kebab
// menu (Edit / Convert to Investor / View record in CRM) and the edit drawer.
//
// Callers own the data: pass the fetched rows plus the grid callbacks, the
// filter bar node, the column set (LP360 swaps the Account column for Entity),
// and onRefresh for whatever else should reload after a save or conversion.
const FundraisingTableCard = ({
	title,
	totalCount,
	filterBar = null,
	columnDefs = FUNDRAISING_COLUMNS,
	getAccountLink,
	rows,
	loading,
	loadingMore = false,
	hasMore = false,
	error = null,
	errorMessage = 'Unable to load Fundraising records',
	sortModel,
	onSortModelChange,
	onRowsScrollEnd,
	noRowsMessage,
	dataTestId = 'fundraisings-grid',
	cardTestId,
	onRefresh,
	onMenuAction,
}) => {
	const { tenant } = useIRWorkstation();
	const { showNotification } = useNotificationContext();

	const [menuAnchorEl, setMenuAnchorEl] = useState(null);
	const [selectedFundraising, setSelectedFundraising] = useState(null);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const menuOpen = Boolean(menuAnchorEl);

	const handleMenuOpen = useCallback((event, fundraising) => {
		event.stopPropagation();
		setMenuAnchorEl(event.currentTarget);
		setSelectedFundraising(fundraising);
	}, []);

	const columns = useMemo(
		() =>
			buildFundraisingGridColumns(handleMenuOpen, getAccountLink, columnDefs),
		[handleMenuOpen, getAccountLink, columnDefs]
	);

	const handleMenuClose = () => {
		setMenuAnchorEl(null);
		setSelectedFundraising(null);
	};

	const handleMenuEdit = () => {
		onMenuAction?.('edit');
		if (selectedFundraising) {
			setDrawerOpen(true);
		}
		// Close menu but don't reset selectedFundraising as data is needed for drawer
		setMenuAnchorEl(null);
	};

	const handleConvert = async () => {
		onMenuAction?.('convertToInvestor');
		const fundraisingToConvert = selectedFundraising;
		handleMenuClose();

		if (!fundraisingToConvert) return;

		showNotification('Converting to Investor Commitment...', 'info');

		try {
			await salesforceFundraisingsService.convertFundraising(
				fundraisingToConvert.id
			);
			showNotification(
				'Fundraising converted to Investor Commitment',
				'success'
			);
			onRefresh?.();
		} catch (err) {
			showNotification(
				err?.message || 'Failed to convert fundraising',
				'error'
			);
		}
	};

	// The Lightning URL is built server-side (SalesforceUrlBuilderService) and
	// returned as salesforce_url, so the CRM URL format lives in one place.
	const handleViewInCrm = () => {
		onMenuAction?.('viewInCrm');
		const url = selectedFundraising?.salesforce_url;
		handleMenuClose();

		if (!url) {
			showNotification('Salesforce record URL is unavailable', 'error');
			return;
		}

		window.open(url, '_blank', 'noopener,noreferrer');
	};

	return (
		<Box
			data-testid={cardTestId}
			sx={{
				width: '100%',
				minWidth: 0,
				flexShrink: 0,
				backgroundColor: 'background.paper',
				border: '0.5px solid',
				borderColor: 'border.light',
				borderRadius: '12px',
				overflow: 'hidden',
			}}
		>
			<Box
				sx={{
					padding: '10px 14px',
					borderBottom: '0.5px solid',
					borderColor: 'border.light',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: '14px',
				}}
			>
				<Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
					{title}
				</Typography>
				{loading ? (
					<Skeleton variant='text' width={100} height={16} />
				) : (
					totalCount != null && (
						<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
							{totalCount} total fundraisings
						</Typography>
					)
				)}
			</Box>
			{filterBar}
			{error ? (
				<Box
					sx={{
						minHeight: 200,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<Typography color='error' sx={{ fontSize: '12px' }}>
						{errorMessage}
					</Typography>
				</Box>
			) : (
				<IRDataGrid
					rows={rows}
					columns={columns}
					loading={loading}
					loadingMore={loadingMore}
					hasMore={hasMore}
					sortModel={sortModel}
					onSortModelChange={onSortModelChange}
					onRowsScrollEnd={onRowsScrollEnd}
					noRowsMessage={noRowsMessage}
					dataTestId={dataTestId}
				/>
			)}
			{selectedFundraising && drawerOpen && (
				<FundraisingDrawer
					open={drawerOpen}
					fundraising={selectedFundraising}
					tenant={tenant}
					onClose={() => {
						setDrawerOpen(false);
						setSelectedFundraising(null);
					}}
					onSaved={onRefresh}
				/>
			)}
			<Menu
				anchorEl={menuAnchorEl}
				open={menuOpen}
				onClose={handleMenuClose}
				PaperProps={{
					elevation: 2,
					sx: {
						minWidth: 150,
						maxWidth: 200,
					},
				}}
			>
				<MenuItem onClick={handleMenuEdit}>
					<ListItemText>Edit</ListItemText>
				</MenuItem>
				{selectedFundraising?.active && (
					<MenuItem onClick={handleConvert}>
						<ListItemText>Convert to Investor</ListItemText>
					</MenuItem>
				)}
				<MenuItem onClick={handleViewInCrm}>
					<ListItemText>View record in CRM</ListItemText>
				</MenuItem>
			</Menu>
		</Box>
	);
};

FundraisingTableCard.propTypes = {
	title: PropTypes.string.isRequired,
	totalCount: PropTypes.number,
	filterBar: PropTypes.node,
	columnDefs: PropTypes.arrayOf(PropTypes.object),
	getAccountLink: PropTypes.func,
	rows: PropTypes.arrayOf(PropTypes.object).isRequired,
	loading: PropTypes.bool,
	loadingMore: PropTypes.bool,
	hasMore: PropTypes.bool,
	error: PropTypes.string,
	errorMessage: PropTypes.string,
	sortModel: PropTypes.arrayOf(
		PropTypes.shape({
			field: PropTypes.string,
			sort: PropTypes.oneOf(['asc', 'desc']),
		})
	),
	onSortModelChange: PropTypes.func,
	onRowsScrollEnd: PropTypes.func,
	noRowsMessage: PropTypes.string,
	dataTestId: PropTypes.string,
	cardTestId: PropTypes.string,
	onRefresh: PropTypes.func,
	onMenuAction: PropTypes.func,
};

export default FundraisingTableCard;
